<script lang="ts" module>
	export interface Props extends HTMLAttributes<SVGElement> {
		fillPercentage: number;
		theme?: 'yellow' | 'brown' | 'brown-gradient';
	}
	let currentId = 0;
</script>

<script lang="ts">
	import type { HTMLAttributes } from 'svelte/elements';

	let { fillPercentage, theme = 'yellow', ...other }: Props = $props();

	let uniqueId = currentId++;
	let leftWidth = $derived(fillPercentage * 32);
	let rightWidth = $derived((1 - fillPercentage) * 32);

	let gradientColors = $derived({
		start: theme === 'brown' ? '#240D02' : theme === 'brown-gradient' ? '#240D02' : '#FFC700',
		end: theme === 'brown' ? '#240D02' : theme === 'brown-gradient' ? '#818181' : '#FEDE60'
	});

	let rightFillColor = $derived(
		theme === 'brown' ? '#E9E7E6' : theme === 'brown-gradient' ? '#E9E7E6' : '#FEEEB4'
	);

	let leftClipPathId = $derived(`left-w${leftWidth}-${theme}-${uniqueId}`);
	let rightClipPathId = $derived(`right-w${rightWidth}-${theme}-${uniqueId}`);
	let gradientId = $derived(`star-gradient-${theme}-${uniqueId}`);
</script>

<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32" fill="none" {...other}>
	<linearGradient
		id={gradientId}
		x1="16"
		y1="0"
		x2="15.9999"
		y2="32"
		gradientUnits="userSpaceOnUse"
	>
		<stop stop-color={gradientColors.start} />
		<stop offset="1" stop-color={gradientColors.end} />
	</linearGradient>

	<clipPath id={leftClipPathId}>
		<rect x="0" y="0" width={leftWidth} height="32" />
	</clipPath>

	<clipPath id={rightClipPathId}>
		<rect x={leftWidth} y="0" width={rightWidth} height="32" />
	</clipPath>

	<path
		d="M15.9999 25.4406L7.65304 31.7312C7.35983 31.9308 7.06478 32.0192 6.76789 31.9965C6.471 31.9738 6.1987 31.8906 5.95099 31.7469C5.70374 31.6037 5.51554 31.3904 5.3864 31.1071C5.25726 30.8238 5.2545 30.5212 5.37813 30.1993L8.56507 19.8375L0.637358 14.1765C0.333578 13.9873 0.139177 13.7459 0.0541552 13.4521C-0.030407 13.1584 -0.0161605 12.8663 0.0968955 12.5757C0.209952 12.2856 0.381373 12.0521 0.611162 11.8752C0.84095 11.6988 1.11854 11.6106 1.44392 11.6106H11.397L14.6529 1.03331C14.7765 0.711835 14.9559 0.459248 15.1912 0.275548C15.4261 0.0918484 15.6956 0 15.9999 0C16.3041 0 16.5737 0.0918484 16.8085 0.275548C17.0438 0.459248 17.2233 0.711835 17.3469 1.03331L20.6028 11.6106H30.5558C30.8812 11.6106 31.1588 11.6988 31.3886 11.8752C31.6184 12.0521 31.7898 12.2856 31.9029 12.5757C32.0159 12.8663 32.0302 13.1584 31.9456 13.4521C31.8606 13.7459 31.6662 13.9873 31.3624 14.1765L23.4347 19.8375L26.6216 30.1993C26.7453 30.5212 26.7425 30.8238 26.6134 31.1071C26.4842 31.3904 26.296 31.6037 26.0488 31.7469C25.8011 31.8906 25.5288 31.9738 25.2319 31.9965C24.935 32.0192 24.6399 31.9308 24.3467 31.7312L15.9999 25.4406Z"
		fill="url(#{gradientId})"
		clip-path="url(#{leftClipPathId})"
	/>

	<path
		d="M15.9999 25.4406L7.65304 31.7312C7.35983 31.9308 7.06478 32.0192 6.76789 31.9965C6.471 31.9738 6.1987 31.8906 5.95099 31.7469C5.70374 31.6037 5.51554 31.3904 5.3864 31.1071C5.25726 30.8238 5.2545 30.5212 5.37813 30.1993L8.56507 19.8375L0.637358 14.1765C0.333578 13.9873 0.139177 13.7459 0.0541552 13.4521C-0.030407 13.1584 -0.0161605 12.8663 0.0968955 12.5757C0.209952 12.2856 0.381373 12.0521 0.611162 11.8752C0.84095 11.6988 1.11854 11.6106 1.44392 11.6106H11.397L14.6529 1.03331C14.7765 0.711835 14.9559 0.459248 15.1912 0.275548C15.4261 0.0918484 15.6956 0 15.9999 0C16.3041 0 16.5737 0.0918484 16.8085 0.275548C17.0438 0.459248 17.2233 0.711835 17.3469 1.03331L20.6028 11.6106H30.5558C30.8812 11.6106 31.1588 11.6988 31.3886 11.8752C31.6184 12.0521 31.7898 12.2856 31.9029 12.5757C32.0159 12.8663 32.0302 13.1584 31.9456 13.4521C31.8606 13.7459 31.6662 13.9873 31.3624 14.1765L23.4347 19.8375L26.6216 30.1993C26.7453 30.5212 26.7425 30.8238 26.6134 31.1071C26.4842 31.3904 26.296 31.6037 26.0488 31.7469C25.8011 31.8906 25.5288 31.9738 25.2319 31.9965C24.935 32.0192 24.6399 31.9308 24.3467 31.7312L15.9999 25.4406Z"
		fill={rightFillColor}
		clip-path="url(#{rightClipPathId})"
	/>
</svg>
